import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { PageHeading, NtIcon } from '../components/NotionUI';
import { createMeeting, getMeetingCapabilities, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const formatSize = (bytes) => (bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

function MeetingNew({ user, onLogout }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [source, setSource] = useState('paste');
  const [form, setForm] = useState({ title: '', meeting_date: nowLocal(), participants: '', transcript: '' });
  const [audio, setAudio] = useState(null);
  const [caps, setCaps] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    getMeetingCapabilities().then(setCaps).catch(() => {});
  }, []);

  const audioReady = caps?.transcription_reachable;
  const canSubmit = form.title.trim() && (source === 'paste' ? form.transcript.trim().length > 0 : Boolean(audio) && audioReady);

  const pickFile = (file) => {
    if (file) setAudio(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      const payload = { title: form.title.trim(), meeting_date: form.meeting_date, participants: form.participants.trim() };
      if (source === 'paste') payload.transcript = form.transcript;
      else payload.audio = audio;
      const res = await createMeeting(payload);
      navigate(`/meetings/${res.meeting.id}`);
    } catch (err) {
      setError(errorMessage(err, t('meetings.createError')));
      setSaving(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <button className="tpl-back fd-back" type="button" onClick={() => navigate('/meetings')}>
          <NtIcon name="back" /> {t('meetings.backToList')}
        </button>
        <PageHeading icon="mic" title={t('meetings.newTitle')} subtitle={t('meetings.newSubtitle')} />

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={submit} className="tp-form mt-form">
          <section className="tp-section">
            <h3>{t('meetings.sectionInfo')}</h3>
            <div className="tp-fields">
              <div className="form-group tp-full">
                <label>{t('meetings.fieldTitle')}</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('meetings.titlePlaceholder')} required autoFocus />
              </div>
              <div className="form-group">
                <label>{t('meetings.fieldDate')}</label>
                <input type="datetime-local" value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label>{t('meetings.fieldParticipants')}</label>
                <input value={form.participants} onChange={(e) => setForm({ ...form, participants: e.target.value })} placeholder={t('meetings.participantsPlaceholder')} />
              </div>
            </div>
          </section>

          <section className="tp-section">
            <h3>{t('meetings.sectionSource')}</h3>
            <div className="pj-view-toggle mt-source" role="tablist">
              <button type="button" role="tab" aria-selected={source === 'paste'} className={'pj-view-btn tk-view-btn' + (source === 'paste' ? ' pj-view-active' : '')} onClick={() => setSource('paste')}>
                <NtIcon name="list" /> {t('meetings.sourcePaste')}
              </button>
              <button type="button" role="tab" aria-selected={source === 'audio'} className={'pj-view-btn tk-view-btn' + (source === 'audio' ? ' pj-view-active' : '')} onClick={() => setSource('audio')}>
                <NtIcon name="mic" /> {t('meetings.sourceAudio')}
              </button>
            </div>

            {source === 'paste' ? (
              <div className="form-group">
                <textarea
                  className="mt-transcript-input"
                  rows={12}
                  value={form.transcript}
                  onChange={(e) => setForm({ ...form, transcript: e.target.value })}
                  placeholder={t('meetings.pastePlaceholder')}
                />
                <p className="tp-hint">{t('meetings.pasteHint')}</p>
              </div>
            ) : (
              <>
                {caps && !audioReady && (
                  <div className="mt-warning">
                    <strong>{t('meetings.audioOffTitle')}</strong>
                    <span>{caps.transcription ? t('meetings.audioUnreachable') : t('meetings.audioOffBody')}</span>
                  </div>
                )}
                <div
                  className={'mt-drop' + (dragging ? ' mt-drop-on' : '') + (audio ? ' mt-drop-has' : '')}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files?.[0]); }}
                  onClick={() => fileRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileRef.current?.click()}
                >
                  <span className="mt-drop-icon" aria-hidden="true"><NtIcon name="mic" size={26} /></span>
                  {audio ? (
                    <>
                      <strong>{audio.name}</strong>
                      <span className="nt-muted">{formatSize(audio.size)}</span>
                    </>
                  ) : (
                    <>
                      <strong>{t('meetings.dropTitle')}</strong>
                      <span className="nt-muted">{t('meetings.dropHint')}</span>
                    </>
                  )}
                  <input ref={fileRef} type="file" accept="audio/*,video/mp4,video/webm,.m4a,.mp3,.wav,.ogg,.opus,.flac,.aac" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
                </div>
                <p className="tp-hint">{t('meetings.audioPrivacy')}</p>
              </>
            )}
          </section>

          <div className="mt-consent">
            <NtIcon name="priority" size={15} /> {t('meetings.consentNote')}
          </div>

          <div className="tp-savebar">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/meetings')}>{t('common.cancel')}</button>
            <button type="submit" className="btn" disabled={saving || !canSubmit}>
              {saving ? t('meetings.creating') : t('meetings.generate')}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

export default MeetingNew;
